import {
  Avatar,
  Box,
  Button,
  Center,
  CloseButton,
  Divider,
  FileButton,
  Group,
  Image,
  Modal,
  Paper,
  Stack,
  Text,
  Textarea,
} from '@mantine/core';
import { Dropzone, FileRejection } from '@mantine/dropzone';
import { notifications } from '@mantine/notifications';
import { IconSignature } from '@tabler/icons-react';
import { useEffect, useMemo, useState } from 'react';
import { notifyAndLogError } from 'src/UI/common/ui-helpers';
import PageLoader from 'src/UI/loader/PageLoader';
import {
  useAdviserSignature,
  useDeleteAdviserSignature,
  useUpdateAdviserSignoff,
  useUploadAdviserSignature,
} from '../hooks/useAdviserSignature';
import { User } from '../types';
import { roleAsDisplayName } from '../utils';

const MAX_FILE_SIZE_MB = 2;
const MAX_FILE_SIZE_BYTES = MAX_FILE_SIZE_MB * 1024 * 1024;
const ACCEPTED_TYPES = ['image/png', 'image/jpeg'];
const MAX_SIGNOFF_LENGTH = 2000;

type SignatureModalProps = {
  user: User;
  onClose: () => void;
};

export default function SignatureModal({ user, onClose }: SignatureModalProps) {
  const { downloadUrl, hasSignature, signoffText, isLoading, isError } = useAdviserSignature(
    user.id,
  );
  const { uploadSignature, isLoading: isUploading } = useUploadAdviserSignature(user.id);
  const { deleteSignature, isLoading: isDeleting } = useDeleteAdviserSignature(user.id);
  const { updateSignoffText, isLoading: isSavingSignoff } = useUpdateAdviserSignoff(user.id);

  const [stagedFile, setStagedFile] = useState<File | null>(null);
  const [markedForRemoval, setMarkedForRemoval] = useState(false);
  const [signoffDraft, setSignoffDraft] = useState('');

  useEffect(() => {
    setSignoffDraft(signoffText ?? '');
  }, [signoffText]);

  const stagedPreviewUrl = useMemo(
    () => (stagedFile ? URL.createObjectURL(stagedFile) : null),
    [stagedFile],
  );
  useEffect(() => {
    return () => {
      if (stagedPreviewUrl) URL.revokeObjectURL(stagedPreviewUrl);
    };
  }, [stagedPreviewUrl]);

  const displayedImageUrl = stagedPreviewUrl ?? (markedForRemoval ? null : downloadUrl);
  const signoffChanged = signoffDraft.trim() !== (signoffText ?? '');
  const isDirty = !!stagedFile || (markedForRemoval && hasSignature) || signoffChanged;
  const isSaving = isUploading || isDeleting || isSavingSignoff;

  const handleFileSelected = (file: File | null) => {
    if (!file) return;

    if (!ACCEPTED_TYPES.includes(file.type)) {
      notifications.show({
        title: 'Invalid file',
        message: 'The signature must be a PNG or JPEG image.',
        color: 'red',
      });
      return;
    }

    if (file.size > MAX_FILE_SIZE_BYTES) {
      const sizeMB = (file.size / (1024 * 1024)).toFixed(2);
      notifications.show({
        title: 'File too large',
        message: `This file is ${sizeMB}MB — the maximum size is ${MAX_FILE_SIZE_MB}MB.`,
        color: 'red',
      });
      return;
    }

    setStagedFile(file);
    setMarkedForRemoval(false);
  };

  const handleRejectedFiles = (rejections: FileRejection[]) => {
    const rejection = rejections[0];
    if (!rejection) return;
    const isTooLarge = rejection.errors.some(error => error.code === 'file-too-large');
    notifications.show({
      title: isTooLarge ? 'File too large' : 'Invalid file',
      message: isTooLarge
        ? `The maximum file size is ${MAX_FILE_SIZE_MB}MB.`
        : 'The signature must be a PNG or JPEG image.',
      color: 'red',
    });
  };

  const handleRemove = () => {
    setStagedFile(null);
    setMarkedForRemoval(true);
  };

  const handleSave = async () => {
    try {
      if (stagedFile) {
        await uploadSignature(stagedFile);
      } else if (markedForRemoval && hasSignature) {
        await deleteSignature();
      }
      if (signoffChanged) {
        await updateSignoffText(signoffDraft.trim());
      }
      notifications.show({
        title: 'Signature saved',
        message: `The signature details for ${user.firstName} ${user.lastName} have been updated.`,
        color: 'green',
      });
      onClose();
    } catch (e) {
      notifyAndLogError(
        'Failed to save signature',
        'An error occurred while saving the signature details. Please try again.',
        e,
        { userId: user.id },
      );
    }
  };

  const initials = `${user.firstName.charAt(0)}${user.lastName.charAt(0)}`.toUpperCase();

  return (
    <Modal
      opened
      onClose={onClose}
      size={560}
      radius="lg"
      padding={0}
      withCloseButton={false}
      centered
    >
      <Group justify="space-between" align="center" p="lg" pb="md">
        <Group gap="sm">
          <Avatar
            size={44}
            radius="xl"
            styles={{
              root: { border: '1px solid rgba(0, 0, 0, 0.08)' },
              placeholder: {
                color: 'var(--mantine-color-gray-7)',
                backgroundColor: 'var(--mantine-color-gray-0)',
                fontWeight: 600,
              },
            }}
          >
            {initials}
          </Avatar>
          <Stack gap={0}>
            <Text fw={600}>
              {user.firstName} {user.lastName}
            </Text>
            <Text size="xs" c="dimmed">
              {roleAsDisplayName(user.userRole)} · signature and sign-off
            </Text>
          </Stack>
        </Group>
        <CloseButton onClick={onClose} aria-label="Close" />
      </Group>

      <Divider color="gray.2" />

      {isLoading && (
        <Center p="xl">
          <PageLoader absolute={false} />
        </Center>
      )}

      {!isLoading && isError && (
        <Text c="red" p="lg">
          Failed to load the signature for this user. Please try again later.
        </Text>
      )}

      {!isLoading && !isError && (
        <>
          <Stack gap="lg" p="lg">
            <Stack gap={8}>
              <Group justify="space-between" align="center">
                <Text size="sm" fw={600}>
                  Signature image
                </Text>
                {displayedImageUrl && (
                  <Group gap={4}>
                    <FileButton onChange={handleFileSelected} accept={ACCEPTED_TYPES.join(',')}>
                      {props => (
                        <Button {...props} variant="subtle" color="gray" size="compact-xs">
                          Replace
                        </Button>
                      )}
                    </FileButton>
                    <Button variant="subtle" color="red" size="compact-xs" onClick={handleRemove}>
                      Remove
                    </Button>
                  </Group>
                )}
              </Group>

              {displayedImageUrl ? (
                <Paper
                  radius="md"
                  style={{ border: '1px solid var(--mantine-color-gray-2)', overflow: 'hidden' }}
                >
                  <Center h={150} bg="white" pos="relative">
                    <Image
                      src={displayedImageUrl}
                      alt="Signature"
                      fit="contain"
                      mah={104}
                      maw="70%"
                    />
                    <Box
                      pos="absolute"
                      bottom={28}
                      left={40}
                      right={40}
                      style={{ borderBottom: '1px dashed var(--mantine-color-gray-3)' }}
                    />
                  </Center>
                </Paper>
              ) : (
                <Dropzone
                  onDrop={files => handleFileSelected(files[0] ?? null)}
                  onReject={handleRejectedFiles}
                  accept={ACCEPTED_TYPES}
                  maxSize={MAX_FILE_SIZE_BYTES}
                  multiple={false}
                  radius="md"
                  p={0}
                  style={{ border: '1.5px dashed var(--mantine-color-gray-4)' }}
                >
                  <Center h={150}>
                    <Stack gap={4} align="center">
                      <IconSignature size={28} stroke={1.5} color="var(--mantine-color-gray-5)" />
                      <Text size="sm" fw={500}>
                        Drag and drop a signature image
                      </Text>
                      <Text size="xs" c="dimmed">
                        or click to browse · PNG or JPEG, up to {MAX_FILE_SIZE_MB}MB
                      </Text>
                    </Stack>
                  </Center>
                </Dropzone>
              )}

              {stagedFile && (
                <Text size="xs" c="dimmed">
                  New image selected — it will replace the current signature when you save.
                </Text>
              )}
              {!stagedFile && markedForRemoval && hasSignature && (
                <Text size="xs" c="dimmed">
                  The signature will be removed when you save.
                </Text>
              )}
            </Stack>

            <Stack gap={8}>
              <Group justify="space-between" align="flex-end">
                <Box>
                  <Text size="sm" fw={600}>
                    Sign-off text
                  </Text>
                  <Text size="xs" c="dimmed">
                    Shown beneath the signature in documents — typically name, role and contact
                    details
                  </Text>
                </Box>
                <Text size="xs" c="dimmed">
                  {signoffDraft.length}/{MAX_SIGNOFF_LENGTH}
                </Text>
              </Group>
              <Textarea
                aria-label="Sign-off text"
                placeholder={'Jane Smith\nFinancial adviser\njane.smith@example.co.uk'}
                value={signoffDraft}
                onChange={e => setSignoffDraft(e.currentTarget.value)}
                autosize
                minRows={3}
                maxRows={6}
                maxLength={MAX_SIGNOFF_LENGTH}
              />
            </Stack>
          </Stack>

          <Divider color="gray.2" />

          <Group justify="flex-end" gap="sm" p="lg" pt="md">
            <Button variant="default" onClick={onClose} disabled={isSaving}>
              Cancel
            </Button>
            <Button
              variant="filled-pill-black"
              onClick={handleSave}
              loading={isSaving}
              disabled={!isDirty}
            >
              Save changes
            </Button>
          </Group>
        </>
      )}
    </Modal>
  );
}
